/**
 * Tiny deterministic RNG (mulberry32). The whole point of carrying a seed in
 * GameState is that shuffles and draws are reproducible: the same seed + same
 * actions always yield the same game, which makes replays and a future
 * authoritative server trivial. We never use Math.random in core.
 */

/** Advance the state and return [nextState, float in [0,1)]. */
export function nextRandom(state: number): [number, number] {
  let t = (state + 0x6d2b79f5) | 0;
  let next = t;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return [next >>> 0, value];
}

/** Return [nextState, integer in [0, max)]. */
export function nextInt(state: number, max: number): [number, number] {
  const [s, v] = nextRandom(state);
  return [s, Math.floor(v * max)];
}

/**
 * Fisher-Yates shuffle driven by the seeded RNG. Returns a new array and the
 * advanced RNG state; the input is not mutated.
 */
export function shuffle<T>(items: readonly T[], state: number): [T[], number] {
  const arr = items.slice();
  let s = state;
  for (let i = arr.length - 1; i > 0; i--) {
    let j: number;
    [s, j] = nextInt(s, i + 1);
    const a = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = a;
  }
  return [arr, s];
}

/** Derive a numeric seed from an arbitrary string (e.g. a game id). */
export function seedFromString(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
